import { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { TileBadge } from '@rocco/ui/tile-shared/components/TileBadge';

import { ProductTileVariants } from './ProductTileVariants';
import { ProductTilePrice } from './ProductTilePrice';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { isSalesModeAtom } from '@rocco/states/apAdminUiContext';
import { useTracker } from '../../../../../tracker';
import { trackTileMediaDetailClick } from '../../../../../web/app/modules/tracking/util/trackTileMedia';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';

export const ProductTile = (props: TileViewModel<ProductTileViewModel>) => {
    const {
        aspectRatio,
        model: product,
        section,
        disableDesignBoard,
        enableTileSlider,
        isFollowed,
        onFollow,
        hideFollowButton = false,
        followButton,
        overlay,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
        contentEditable,
        eager,
    } = props;
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);
    const tracker = useTracker();
    const isSalesMode = useAtomValue(isSalesModeAtom);

    const draftOrPending = product.draft || product.pendingApproval;
    const statusLabel = draftOrPending
        ? product.draft
            ? 'Draft'
            : 'Pending Approval'
        : product.badge;

    const handleTileClick = () => {
        onClick?.();
        if (disableLink) {
            return;
        }

        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            product.id,
            product.professional.id,
            section,
        );

        trackTileMediaDetailClick(tracker, {
            itemID: product.id,
            itemType: 'ProductTile',
            itemName: product.title,
        });
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            to={product.link}
            prefetch="intent"
            onClick={handleTileClick}
        >
            <div className={cn('flex h-full flex-col gap-4', className)}>
                <div className="group relative h-auto">
                    <TileMedia
                        aspectRatio={aspectRatio}
                        images={product.images}
                        video={product.video}
                        enableTileSlider={enableTileSlider}
                        eager={eager}
                        onSlideChange={(direction) => {
                            tileActions?.trackingConfig?.onTileMediaSliderSlideChange?.(
                                direction,
                                product.id,
                            );
                        }}
                    />

                    {overlay ? (
                        overlay
                    ) : (
                        <>
                            <div className="absolute inset-0 hidden rounded-tile bg-gray-900/10 opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100" />
                            <div className="absolute top-3.5 right-4 flex gap-1 transition-opacity duration-300 md:invisible md:opacity-0 md:group-hover:visible md:group-hover:opacity-100">
                                {followButton
                                    ? followButton
                                    : !hideFollowButton &&
                                      !disableDesignBoard && (
                                          <TileFollowButton
                                              pinId={product.id}
                                              pinType="ProductPin"
                                              isFollowed={isFollowed}
                                              onFollow={onFollow}
                                          />
                                      )}
                            </div>
                        </>
                    )}

                    {statusLabel && (
                        <div className="absolute top-4 left-4">
                            <TileBadge title={statusLabel} />
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-4">
                    {inlineActionButtons && (
                        <div className="flex flex-col">
                            {inlineActionButtons}
                        </div>
                    )}
                    <div
                        className={cn(
                            TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                            'flex flex-col',
                        )}
                    >
                        <span
                            contentEditable={contentEditable?.enabled.title}
                            role={
                                contentEditable?.enabled.title
                                    ? 'textbox'
                                    : undefined
                            }
                            aria-label={
                                contentEditable?.enabled.title
                                    ? 'Edit title'
                                    : undefined
                            }
                            aria-multiline="false"
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'title',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                'overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis md:text-base md:tracking-[0.02rem]',
                                contentEditable?.enabled.title
                                    ? 'truncate'
                                    : 'line-clamp-1',
                                contentEditable?.enabled.title &&
                                    'focus-within:border-primary-500 -mx-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                            )}
                        >
                            {product.title}
                        </span>
                        <span
                            contentEditable={
                                contentEditable?.enabled.description
                            }
                            role={
                                contentEditable?.enabled.description
                                    ? 'textbox'
                                    : undefined
                            }
                            aria-label={
                                contentEditable?.enabled.description
                                    ? 'Edit description'
                                    : undefined
                            }
                            aria-multiline="false"
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'description',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                'overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis text-gray-900/40 md:text-base md:tracking-[0.02rem]',
                                !contentEditable?.enabled.description &&
                                    'line-clamp-1',
                                contentEditable?.enabled.description &&
                                    'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                            )}
                        >
                            {product.professional.name}
                        </span>
                    </div>

                    {product.price && !isSalesMode ? (
                        <div className="flex items-center justify-between gap-2 text-sm text-gray-900/40 md:text-base">
                            <ProductTilePrice price={product.price} />
                            {product.variants && (
                                <ProductTileVariants
                                    variants={product.variants}
                                    limit={isMobile ? 3 : 5}
                                />
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
        </OptionalLink>
    );
};
